import * as React from 'react'
import * as PropTypes from 'prop-types'
import { css, hover, Rule } from 'glamor'

import { useTheme } from '../../theme'
import { isKey } from '../../utils'

import Checkmark from './checkmark.svg'
import getStyles from './styles'

const itemVariants = ['primary', 'secondary', 'title', 'cta']

export interface IItemProps<TValue> {
  label?: React.ReactNode
  variant?: 'primary' | 'secondary' | 'title' | 'cta'
  checklistItem?: boolean
  disabled?: boolean
  checked?: boolean
  selected?: boolean
  value?: TValue
  style?: Rule
  wrapper?: string | React.ComponentType<{}>
  onClick?: (event: React.SyntheticEvent, isNotChecklistItem: boolean) => void
}

function Item<TValue>({
  label,
  variant,
  style: styleProp,
  checklistItem,
  checked,
  onClick,
  wrapper: Wrapper,
  selected,
  disabled,
  ...additional
}: IItemProps<TValue>) {
  const styles = getStyles(useTheme())

  if (!Wrapper) {
    return null
  }

  const interactiveProps = onClick
    ? {
        onClick: (event: React.SyntheticEvent) => {
          !disabled && onClick && onClick(event, !checklistItem)
        },
        onKeyPress: (event: React.KeyboardEvent) =>
          (event.key === 'Enter' || event.key === ' ') && onClick(event, !checklistItem),
        role: 'button',
        tabIndex: 0,
      }
    : {}

  return (
    <Wrapper
      {...additional}
      {...css(
        styles.base,
        isKey(styles.variants, variant) && styles.variants[variant],
        checklistItem && styles.isChecklistItem,
        checked && styles.checked,
        selected && styles.selected,
        disabled && styles.disabled,
        styleProp
      )}
      {...(disabled ? {} : hover(styles.hover))}
    >
      <div {...css(styles.button)} {...interactiveProps}>
        <div
          {...css({
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          })}
        >
          {label}
        </div>
        {checklistItem && checked && (
          <div {...css({ flexShrink: 0 })}>
            <Checkmark {...css(styles.checkIcon)} />
          </div>
        )}
      </div>
    </Wrapper>
  )
}

Item.propTypes = {
  label: PropTypes.node,
  variant: PropTypes.oneOf(itemVariants),
  checklistItem: PropTypes.bool,
  disabled: PropTypes.bool,
  checked: PropTypes.bool,
  selected: PropTypes.bool,
  value: PropTypes.any,
  style: PropTypes.object,
  wrapper: PropTypes.string,
  onClick: PropTypes.func,
}

Item.defaultProps = {
  variant: 'secondary',
  wrapper: 'li',
}

Item.displayName = 'DropdownItem'

export default Item
